"use client";

import { useEffect, useState, type MouseEvent, type PointerEvent } from "react";
import { useRouter } from "next/navigation";
import type { ParticleType, Particle } from "./FactionGrid";
import FactionFX from "./FactionFX";
import { playHover, playSelection, preloadFactionSounds, type FactionKey } from "./sounds";
import styles from "../faction.module.css";

type FactionData = {
  key: FactionKey;
  className: string;
  href: string;
  ariaLabel: string;
  heroSrc: string;
  heroAlt: string;
  iconSrc: string;
  iconAlt: string;
  kicker: string;
  heading: [string, string];
  enterLabel: string;
  desc: string;
};

export const FACTIONS: Record<FactionKey, FactionData> = {
  angel: {
    key: "angel",
    className: styles.angel,
    href: "/faction/angelarmy",
    ariaLabel: "Enter Angel Army",
    heroSrc: "/faction/angel-hero.png",
    heroAlt: "Angel Army featured warrior art",
    iconSrc: "/faction/angel-icon.png",
    iconAlt: "Angel Army icon",
    kicker: "Angel Army",
    heading: ["Enter The", "Divine Realm"],
    enterLabel: "Enter Angel Army",
    desc: "Step beyond the mortal plane and enter the sacred territory of the Angel Army.",
  },
  demon: {
    key: "demon",
    className: styles.demon,
    href: "/faction/demontime",
    ariaLabel: "Enter Dojo Demon Time",
    heroSrc: "/faction/demon-hero.png",
    heroAlt: "Dojo Demon Time featured warrior art",
    iconSrc: "/faction/demon-icon.png",
    iconAlt: "Dojo Demon Time icon",
    kicker: "Dojo Demon Time",
    heading: ["Enter The", "Dinner Table"],
    enterLabel: "Enter Demon Time",
    desc: "Chaos, power and loyalty collide at the table where Demons gather.",
  },
  mortal: {
    key: "mortal",
    className: styles.mortal,
    href: "/faction/mortalkombatants",
    ariaLabel: "Enter Mortal Kombatants",
    heroSrc: "/faction/mortal-hero.png",
    heroAlt: "Mortal Kombatants featured warrior art",
    iconSrc: "/faction/mortal-icon.png",
    iconAlt: "Mortal Kombatants icon",
    kicker: "Mortal Kombatants",
    heading: ["Enter The", "Mortal Temple"],
    enterLabel: "Enter Mortal Kombatants",
    desc: "Technique, willpower and human resolve define the warriors of the Mortal Temple.",
  },
  security: {
    key: "security",
    className: styles.security,
    href: "/faction/dojosecurity",
    ariaLabel: "Enter Dojo Security",
    heroSrc: "/faction/security-hero.png",
    heroAlt: "Dojo Security featured warrior art",
    iconSrc: "/faction/security-icon.png",
    iconAlt: "Dojo Security icon",
    kicker: "Dojo Security",
    heading: ["Enter", "The Void"],
    enterLabel: "Enter Dojo Security",
    desc: "Agents operate beyond the normal battlefield, protecting the Dojo from the shadows.",
  },
  ronin: {
    key: "ronin",
    className: styles.ronin,
    href: "/faction/ronin",
    ariaLabel: "Enter Ronin",
    heroSrc: "/faction/ronin-hero.png",
    heroAlt: "Ronin featured warrior art",
    iconSrc: "/faction/ronin-icon.png",
    iconAlt: "Ronin icon",
    kicker: "Ronin",
    heading: ["Walk", "Alone"],
    enterLabel: "Enter Ronin",
    desc: "No army. No temple. No master. The Ronin walk their own path through The Rapture.",
  },
};

type Props = {
  faction: FactionKey;
  particles: Record<ParticleType, Particle[]> | null;
  /** Phones: the card in view holds the desktop "hover" look (set by the grid). */
  lit?: boolean;
};

export default function FactionCard({ faction, particles, lit = false }: Props) {
  const data = FACTIONS[faction];
  const router = useRouter();
  const [selecting, setSelecting] = useState(false);
  useEffect(() => { preloadFactionSounds(); }, []);

  // Click = confirm blip + the faction's voice line, then navigate when the line
  // ends (capped at 4 s). A second click, or a modifier/middle click, goes straight through.
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || selecting) return;
    e.preventDefault();
    setSelecting(true);
    const go = () => { if (data.href.startsWith("/")) router.push(data.href); else window.location.assign(data.href); };
    playSelection(faction).then(go);
  };

  return (
    <a className={`${styles.faction} ${data.className} ${lit ? styles.lit : ""} ${selecting ? styles.selecting : ""}`} href={data.href} aria-label={data.ariaLabel}
      data-faction={faction} aria-busy={selecting || undefined}
      // A finger "entering" a card is the tap itself; on phones the blip comes from the grid's swipe focus, not pointerenter.
      onPointerEnter={(e: PointerEvent<HTMLAnchorElement>) => { if (e.pointerType !== "touch") playHover(); }} onFocus={playHover} onClick={onClick}>
      <div className={styles.scene} aria-hidden="true" />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className={styles.heroArt} src={data.heroSrc} alt={data.heroAlt} />

      <FactionFX faction={faction} particles={particles} />

      <div className={styles.separatorLine} aria-hidden="true" />

      <div className={styles.content}>
        <div className={styles.symbol}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={data.iconSrc} alt={data.iconAlt} />
        </div>
        <div className={styles.kicker}>{data.kicker}</div>
        <h2 className={styles.heading}>
          {data.heading[0]}
          <br />
          {data.heading[1]}
        </h2>
        <div className={styles.enter}>{data.enterLabel}</div>
        <div className={styles.desc}>{data.desc}</div>
      </div>
    </a>
  );
}
